"use client";

import { useState } from "react";
import {
  Calendar,
  Clock,
  Database,
  Globe,
  GraduationCap,
  Shield,
  Lock,
  AlertTriangle,
} from "lucide-react";
import Footer from "../Header/Footer";
import Navbar from "../Header/Navbar";
import ApplyButton from "../common/ApplyButton";

const BsCyberSecurityPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showAllSemesters, setShowAllSemesters] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="font-sans">
      {/* Header */}
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-800 to-red-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Bachelor of Science in Cyber Security
              </h1>
              <p className="text-xl mb-6 text-red-100">
                Defend the digital frontier and secure the future
              </p>
              <div className="flex flex-wrap gap-4">
                <ApplyButton
                  to="/login"
                  className="bg-white text-red-700 hover:bg-red-50 font-bold py-3 px-6 rounded-lg transition duration-300 shadow-lg transform hover:scale-105"
                >
                  Apply Now
                </ApplyButton>
              </div>
            </div>
            <div
              className="md:w-1/2"
              style={{ height: "30rem", width: "43rem" }}
            >
              <img
                style={{ objectFit: "cover", height: "100%", width: "100%" }}
                src="https://images.unsplash.com/photo-1618060932014-4deda4932554?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="BS Cyber Security Students"
                className="rounded-lg shadow-2xl transform hover:scale-105 transition duration-300  h-99 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info */}
      <section className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <Calendar className="text-red-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Program Duration
              </h3>
              <p className="text-gray-600">4 Years (8 Semesters)</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <GraduationCap className="text-red-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Degree Awarded
              </h3>
              <p className="text-gray-600">BS in Cyber Security</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <Clock className="text-red-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Credit Hours
              </h3>
              <p className="text-gray-600">135 Credit Hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Program Tabs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">
              Program Details
            </h2>
            <p className="text-center text-gray-600 max-w-5xl mx-auto text-lg">
              The Bachelor of Science in Cyber Security (BSCY) at NIIT, Lahore,
              is a 4-year HEC-recognized degree program comprising 130 credit
              hours across 8 semesters. It is designed to equip students with
              the skills and knowledge necessary to protect digital
              infrastructure, networks, and sensitive information against
              evolving cyber threats. The curriculum includes in-depth coverage
              of security protocols, ethical hacking, risk assessment, and
              incident response. Practical lab training and industry
              collaborations ensure real-world readiness for cybersecurity
              roles.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center border-b border-gray-200 mb-8">
            {["overview", "curriculum", "careers"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-6 font-medium text-sm focus:outline-none transition duration-300 ${
                  activeTab === tab
                    ? "border-b-2 border-red-600 text-red-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white p-8 rounded-lg shadow-lg">
            {activeTab === "overview" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Program Overview
                </h3>
                <p className="text-gray-700 mb-6">
                  The BSCY program provides a comprehensive education in the
                  principles and practices of information security, combining
                  technical training with a strong understanding of
                  cybersecurity policy and governance. It prepares students for
                  careers in digital forensics, penetration testing, and
                  information assurance through rigorous coursework and hands-on
                  experiences.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {[
                    {
                      icon: <Shield className="text-red-600" size={36} />,
                      title: "Comprehensive Security Training",
                      description:
                        "Acquire skills to identify, analyze, and defend against a wide range of cyber threats and vulnerabilities across systems and networks.",
                    },
                    {
                      icon: <Lock className="text-red-600" size={36} />,
                      title: "Hands-on Labs",
                      description:
                        "Apply concepts in modern cybersecurity labs using simulated attacks and defensive scenarios to build real-world capabilities.",
                    },
                    {
                      icon: (
                        <AlertTriangle className="text-red-600" size={36} />
                      ),
                      title: "Ethical Hacking",
                      description:
                        "Learn penetration testing, vulnerability scanning, and ethical hacking techniques essential for securing digital environments.",
                    },
                    {
                      icon: <Globe className="text-red-600" size={36} />,
                      title: "Industry Partnerships",
                      description:
                        "Engage with leading cybersecurity firms through internships, guest lectures, and collaborative projects for career development.",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start bg-gray-50 p-6 rounded-lg transform hover:scale-105 transition duration-300"
                    >
                      <div className="bg-white p-3 rounded-full mr-4 shadow-md">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 text-lg mb-2">
                          {item.title}
                        </h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "curriculum" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Curriculum
                </h3>
                <p className="text-gray-700 mb-6">
                  Our BS Cyber Security curriculum provides a strong foundation
                  in computer science and specialized knowledge in cybersecurity
                  principles and practices. The program is structured over 8
                  semesters, with a total of 130 credit hours.
                </p>

                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Course Structure
                  </h4>

                  {[
                    // Semester 1
                    {
                      semester: 1,
                      courses: [
                        {
                          sNo: 1,
                          code: "EL170",
                          title: "Functional English",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "CY104",
                          title: "Introduction to Cyber Security",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 3,
                          code: "CY104L",
                          title: "Introduction to Cyber Security Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 4,
                          code: "CS181",
                          title:
                            "Application of Information and Communication Technologies",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 5,
                          code: "CS181L",
                          title:
                            "Application of Information and Communication Technologies Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 6,
                          code: "CS111",
                          title: "Programming Fundamentals",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 7,
                          code: "CS111L",
                          title: "Programming Fundamentals Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 8,
                          code: "HU124",
                          title: "Islamic Studies and Ethics",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 9,
                          code: "MA216",
                          title: "Discrete Structures",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 10,
                          code: "MA114",
                          title: "Foundational Mathematics (Non-Credit)",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 15,
                      totalLab: 3,
                      totalCredits: 18,
                    },
                    // Semester 2
                    {
                      semester: 2,
                      courses: [
                        {
                          sNo: 1,
                          code: "SE101",
                          title: "Introduction to Software Engineering",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "CS112",
                          title: "Object Oriented Programming",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 3,
                          code: "CS112L",
                          title: "Object Oriented Programming Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 4,
                          code: "EE123",
                          title: "Digital Logic Design",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 5,
                          code: "EE123L",
                          title: "Digital Logic Design Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 6,
                          code: "PH111",
                          title: "Applied Physics",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 7,
                          code: "PH111L",
                          title: "Applied Physics Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 8,
                          code: "MA201",
                          title: "Linear Algebra",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 9,
                          code: "MA118",
                          title: "Pre-Calculus (Non-Credit)",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 13,
                      totalLab: 3,
                      totalCredits: 16,
                    },
                    // Semester 3
                    {
                      semester: 3,
                      courses: [
                        {
                          sNo: 1,
                          code: "CY371",
                          title: "Information Assurance",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "CS260",
                          title: "Computer Networks",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 3,
                          code: "CS260L",
                          title: "Computer Networks Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 4,
                          code: "CS216",
                          title: "Data Structures",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 5,
                          code: "CS216L",
                          title: "Data Structures Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 6,
                          code: "MA110",
                          title: "Calculus and Analytical Geometry",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 7,
                          code: "CS226",
                          title: "Computer Organization and Assembly Language",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 8,
                          code: "CS226L",
                          title:
                            "Computer Organization and Assembly Language Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 9,
                          code: "BA152",
                          title: "Introduction to Management",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                      ],
                      totalTheory: 15,
                      totalLab: 3,
                      totalCredits: 18,
                    },
                    // Semester 4
                    {
                      semester: 4,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS325",
                          title: "Operating Systems",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 2,
                          code: "CS325L",
                          title: "Operating Systems Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "CY256",
                          title: "Secure Software Design and Development",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "CY256L",
                          title: "Secure Software Design and Development Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "CY223",
                          title: "Network Security",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 6,
                          code: "CY223L",
                          title: "Network Security Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 7,
                          code: "CYXXX",
                          title: "Cyber Security Elective I",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 8,
                          code: "CYXXXL",
                          title: "Cyber Security Elective I Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 9,
                          code: "MA105",
                          title: "Multivariable Calculus",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 12,
                      totalLab: 4,
                      totalCredits: 16,
                    },
                    // Semester 5
                    {
                      semester: 5,
                      courses: [
                        {
                          sNo: 1,
                          code: "CY334",
                          title: "Digital Forensics",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 2,
                          code: "CY334L",
                          title: "Digital Forensics Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "CS426",
                          title: "Parallel and Distributed Computing",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "CS426L",
                          title: "Parallel and Distributed Computing Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "EL271",
                          title: "Expository Writing",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 6,
                          code: "CS344",
                          title: "Artificial Intelligence",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 7,
                          code: "CS344L",
                          title: "Artificial Intelligence Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 8,
                          code: "CYXXX",
                          title: "Cyber Security Elective II",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 9,
                          code: "CYXXXL",
                          title: "Cyber Security Elective II Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 10,
                          code: "HU411",
                          title: "Civics and Community Engagements",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                      ],
                      totalTheory: 13,
                      totalLab: 4,
                      totalCredits: 17,
                    },
                    // Semester 6
                    {
                      semester: 6,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS230",
                          title: "Database Systems",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "CS230L",
                          title: "Database Systems Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "CYXXX",
                          title: "Cyber Security Elective III",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "CYXXXL",
                          title: "Cyber Security Elective III Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "CYXXX",
                          title: "Cyber Security Elective IV",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 6,
                          code: "CYXXXL",
                          title: "Cyber Security Elective IV Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 7,
                          code: "EL400",
                          title: "Technical and Business Writing",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 8,
                          code: "BA353",
                          title: "Entrepreneurship",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 9,
                          code: "CY491",
                          title: "Final Year Project-I",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                      ],
                      totalTheory: 12,
                      totalLab: 4,
                      totalCredits: 16,
                    },
                    // Semester 7
                    {
                      semester: 7,
                      courses: [
                        {
                          sNo: 1,
                          code: "MA301",
                          title: "Probability and Statistics",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "MK101",
                          title: "Principles of Marketing",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 3,
                          code: "CS332",
                          title: "Design and Analysis of Algorithms",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 4,
                          code: "CYXXX",
                          title: "Cyber Security Elective V",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 5,
                          code: "CYXXXL",
                          title: "Cyber Security Elective V Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 6,
                          code: "CYXXX",
                          title: "Cyber Security Elective VI",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 7,
                          code: "CYXXXL",
                          title: "Cyber Security Elective VI Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 8,
                          code: "CY492",
                          title: "Final Year Project –II",
                          theory: 0,
                          lab: 2,
                          total: 2,
                        },
                      ],
                      totalTheory: 13,
                      totalLab: 4,
                      totalCredits: 17,
                    },
                    // Semester 8
                    {
                      semester: 8,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS470",
                          title: "Professional Practices",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 2,
                          code: "CYXXX",
                          title: "Cyber Security Elective VII",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 3,
                          code: "CYXXXL",
                          title: "Cyber Security Elective VII Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 4,
                          code: "HU405",
                          title: "Ideology and Constitution of Pakistan",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 5,
                          code: "CS215",
                          title: "Information Security",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 6,
                          code: "CS215L",
                          title: "Information Security Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 7,
                          code: "CY493",
                          title: "Final Year Project – III",
                          theory: 0,
                          lab: 3,
                          total: 3,
                        },
                      ],
                      totalTheory: 8,
                      totalLab: 5,
                      totalCredits: 13,
                    },
                  ]
                    .filter(
                      (semester) => showAllSemesters || semester.semester <= 2
                    )
                    .map((semester) => (
                      <div key={semester.semester} className="mb-8">
                        <h5 className="text-lg font-semibold text-red-700 mb-3">
                          Semester {semester.semester}
                        </h5>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  S No
                                </th>
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  Course Code
                                </th>
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  Course Title
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Theory
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Lab
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {semester.courses.map((course) => (
                                <tr
                                  key={course.sNo}
                                  className="hover:bg-gray-50"
                                >
                                  <td className="py-2 px-4 border-b text-sm">
                                    {course.sNo}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm">
                                    {course.code}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm">
                                    {course.title}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm text-center">
                                    {course.theory}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm text-center">
                                    {course.lab}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm text-center">
                                    {course.total}
                                  </td>
                                </tr>
                              ))}
                              <tr className="bg-red-50 font-medium">
                                <td
                                  className="py-2 px-4 border-b text-sm"
                                  colSpan="3"
                                >
                                  Total Credit Hours
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  {semester.totalTheory}
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  {semester.totalLab}
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  {semester.totalCredits}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}

                  {!showAllSemesters && (
                    <div className="flex justify-center mt-8">
                      <button
                        onClick={() => setShowAllSemesters(true)}
                        className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg transition duration-300 shadow-md"
                      >
                        Show More Semesters
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-8 bg-red-50 p-6 rounded-lg shadow-md">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Program Summary
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h5 className="font-medium text-gray-800 mb-2">
                        Total Credit Hours
                      </h5>
                      <ul className="list-disc pl-5 space-y-1 text-gray-700">
                        <li>Theory Courses: 101 credit hours</li>
                        <li>Lab Courses: 30 credit hours</li>
                        <li>Total: 131 credit hours</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-800 mb-2">
                        Course Distribution
                      </h5>
                      <ul className="list-disc pl-5 space-y-1 text-gray-700">
                        <li>Core Courses: 40 courses</li>
                        <li>Cyber Security Elective Courses: 14 courses</li>
                        <li>General Education: 12 courses</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-gray-600">
                    <p>
                      * 3 Credit hours internship (Student can apply for 6-8
                      weeks supervised internship course)
                    </p>
                    <p>* Applicable from Fall 2025 Intake & Onwards</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "admission" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Admission Requirements
                </h3>
                <p className="text-gray-700 mb-6">
                  To be eligible for admission to the BS Cyber Security program,
                  applicants must meet the following requirements:
                </p>

                <div className="space-y-8">
                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Academic Requirements
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>
                        Higher Secondary School Certificate (HSSC) or equivalent
                      </li>
                      <li>
                        Strong background in Mathematics and Computer Science is
                        recommended
                      </li>
                      <li>Good academic standing throughout academic career</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Entrance Test
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>
                        Qualifying score in the NIIT Cyber Security Aptitude
                        Test
                      </li>
                      <li>
                        Test covers Mathematics, Logical Reasoning, and Basic
                        Computer Knowledge
                      </li>
                      <li>Sample test papers available on request</li>
                    </ul>
                  </div>

                  {/* <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Required Documents
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>Completed application form</li>
                      <li>
                        Attested copies of academic certificates and transcripts
                      </li>
                      <li>Copy of CNIC or B-Form</li>
                      <li>Four recent passport-sized photographs</li>
                      <li>
                        Character certificate from last attended institution
                      </li>
                    </ul>
                  </div> */}

                  <div className="bg-red-50 p-6 rounded-lg shadow-md border-l-4 border-red-500">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Application Process
                    </h4>
                    <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                      <li>Submit online application through the NIIT portal</li>
                      <li>Pay application fee</li>
                      <li>Appear for entrance test on the scheduled date</li>
                      <li>
                        Shortlisted candidates will be called for interview
                      </li>
                      <li>
                        Final selection based on academic record, entrance test,
                        and interview performance
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "careers" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Career Prospects
                </h3>
                <p className="text-gray-700 mb-6">
                  Graduates of our BS Cyber Security program are well-prepared
                  for diverse and rewarding careers in the rapidly growing field
                  of cybersecurity. Our program equips students with the skills
                  and knowledge needed to protect organizations from cyber
                  threats.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Job Opportunities
                    </h4>
                    <ul className="space-y-4">
                      {[
                        {
                          icon: <Shield size={20} />,
                          title: "Information Security Analyst",
                          description:
                            "Protect computer networks from cyber attacks",
                        },
                        {
                          icon: <AlertTriangle size={20} />,
                          title: "Penetration Tester",
                          description:
                            "Identify and exploit security vulnerabilities",
                        },
                        {
                          icon: <Database size={20} />,
                          title: "Security Operations Center (SOC) Analyst",
                          description:
                            "Monitor and respond to security incidents",
                        },
                        {
                          icon: <Lock size={20} />,
                          title: "Cyber Security Consultant",
                          description:
                            "Advise organizations on security best practices",
                        },
                      ].map((job, index) => (
                        <li key={index} className="flex items-start">
                          <div className="bg-red-100 p-2 rounded-full mr-3 mt-1">
                            {job.icon}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              {job.title}
                            </span>
                            <p className="text-sm text-gray-600">
                              {job.description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Advanced Opportunities
                    </h4>
                    <ul className="space-y-4">
                      {[
                        {
                          icon: <Shield size={20} />,
                          title: "Chief Information Security Officer (CISO)",
                          description:
                            "Lead organization's information security strategy",
                        },
                        {
                          icon: <AlertTriangle size={20} />,
                          title: "Cyber Threat Intelligence Analyst",
                          description: "Analyze and predict cyber threats",
                        },
                        {
                          icon: <Lock size={20} />,
                          title: "Security Architect",
                          description: "Design secure IT infrastructures",
                        },
                        {
                          icon: <Database size={20} />,
                          title: "Digital Forensics Investigator",
                          description:
                            "Investigate cyber crimes and data breaches",
                        },
                      ].map((job, index) => (
                        <li key={index} className="flex items-start">
                          <div className="bg-red-100 p-2 rounded-full mr-3 mt-1">
                            {job.icon}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              {job.title}
                            </span>
                            <p className="text-sm text-gray-600">
                              {job.description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-red-50 p-6 rounded-lg shadow-md mb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Industry Sectors
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      "Financial Services",
                      "Government",
                      "Healthcare",
                      "Telecommunications",
                      "E-commerce",
                      "Defense",
                      "Consulting Firms",
                      "Technology Companies",
                    ].map((sector, index) => (
                      <div
                        key={index}
                        className="bg-white p-3 rounded text-center shadow-sm"
                      >
                        <p className="font-medium text-gray-700">{sector}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Further Education
                  </h4>
                  <p className="text-gray-700 mb-4">
                    BS Cyber Security graduates can pursue advanced degrees and
                    certifications such as:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>Master of Science in Cyber Security</li>
                    <li>Master of Science in Information Security</li>
                    <li>PhD in Cyber Security or related fields</li>
                    <li>
                      Professional certifications: CISSP, CEH, CompTIA
                      Security+, CISM
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-20 bg-gradient-to-r from-red-800 to-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Become a Cyber Security Expert?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join our BS Cyber Security program and develop the skills to protect
            the digital world from emerging threats.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <ApplyButton
              to="/login"
              className="bg-white text-red-700 hover:bg-red-50 font-bold py-3 px-8 rounded-lg transition duration-300 shadow-lg transform hover:scale-105"
            >
              Apply Now
            </ApplyButton>
          </div>
        </div>
      </section> */}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BsCyberSecurityPage;
